// The four-up live network summary — Routers / OLTs / PPPoE sessions /
// Open issues — fed by GET /v1/api/dashboard/tiles and kept live via the
// fleet SSE channels. The React port of the PHP portal's `#nsStrip` tile
// row; a standalone component so the Search home page and any future
// overview screen share one definition. Built from @noc/ui's StatTile so
// it stays visually identical to every other tile row in the app.
//
// For a reseller-role login the *same* endpoint returns the figures
// scoped to the routers / OLTs an admin granted it (the backend reads the
// request Caller) — so this component renders unchanged; only the numbers
// shrink to that reseller's slice of the fleet.

import { Link } from "react-router-dom";
import { AlertTriangle, Router, Server, Users } from "lucide-react";
import { Alert, AlertDescription, Skeleton, StatTile, type StatTone } from "@noc/ui";
import { useCan } from "@/features/auth/roles";
import { useDashboardTiles, useFleetLiveUpdates } from "./useFleet";

export function FleetTilesStrip({ className }: { className?: string }) {
  // Keep the tiles live via SSE — useFleetLiveUpdates picks a reseller-safe
  // channel set (just the OLT-health aggregates) for reseller logins.
  useFleetLiveUpdates({ tiles: true });
  // Only make a tile a link to a page the caller can actually open (a
  // reseller granted only OLTs can't reach /routers, etc.).
  const canRoutersRead = useCan("routers:read");
  const canOltsRead = useCan("olts:read");
  const canSubscribersRead = useCan("subscribers:read");
  const { data, isPending, isError, error } = useDashboardTiles();
  const t = data?.tiles;
  const v = (s: React.ReactNode) => (isPending ? <Skeleton className="h-7 w-16" /> : s);
  // A reseller granted none of one device kind shows that tile as "none"
  // rather than the (vacuously true) "all online"; admin/support never
  // hit total === 0.
  const fleetSub = (total: number, down: number) => (total === 0 ? "none" : down > 0 ? `${down} down` : "all online");
  const fleetTone = (total: number, down: number): StatTone => (total === 0 ? "default" : down > 0 ? "danger" : "success");

  return (
    <div className={className}>
      {isError && (
        <Alert variant="destructive" className="mb-3">
          <AlertDescription>
            {error instanceof Error ? error.message : "Failed to load network stats."}
          </AlertDescription>
        </Alert>
      )}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Tile
          to={canRoutersRead ? "/routers" : undefined}
          icon={<Router />}
          label="Routers"
          value={v(t ? `${t.routers_online}/${t.routers_total}` : "—")}
          tone={t ? fleetTone(t.routers_total, t.routers_down) : "default"}
          sub={t ? fleetSub(t.routers_total, t.routers_down) : "—"}
        />
        <Tile
          to={canOltsRead ? "/olts" : undefined}
          icon={<Server />}
          label="OLTs"
          value={v(t ? `${t.olts_online}/${t.olts_total}` : "—")}
          tone={t ? fleetTone(t.olts_total, t.olts_down) : "default"}
          sub={t ? fleetSub(t.olts_total, t.olts_down) : "—"}
        />
        <Tile
          to={canSubscribersRead ? "/pppoe-sessions" : undefined}
          icon={<Users />}
          label="PPPoE sessions"
          value={v(t ? t.sessions.toLocaleString() : "—")}
          tone="default"
          sub="online now"
        />
        <Tile
          to={canOltsRead ? "/olt-issues" : undefined}
          icon={<AlertTriangle />}
          label="Open issues"
          value={v(t ? String(t.issues) : "—")}
          tone={t && t.issues > 0 ? "warning" : "success"}
          sub={t && t.issues > 0 ? "needs attention" : "clean"}
        />
      </div>
    </div>
  );
}

function Tile({
  icon,
  label,
  value,
  sub,
  tone,
  to,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  sub: string;
  tone: StatTone;
  to?: string;
}) {
  const tile = <StatTile icon={icon} label={label} value={value} sub={sub} tone={tone} interactive={!!to} />;
  return to ? (
    <Link to={to} className="block">
      {tile}
    </Link>
  ) : (
    tile
  );
}
